import {
  CalendarDaysIcon,
  CalendarIcon,
  ClipboardListIcon,
  CloudIcon,
  DatabaseIcon,
  FileTextIcon,
  GithubIcon,
  HardDriveIcon,
  LayersIcon,
  LayoutGridIcon,
  ListChecksIcon,
  MailIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  PaletteIcon,
  PlugIcon,
  TagIcon,
  UsersIcon,
} from "lucide-react";

import type { ConnectAppCardModel } from "./ConnectAppCard";

export const MORROW_APP_CARDS: ReadonlyArray<ConnectAppCardModel> = [
  { toolkit: "gmail", name: "Gmail", icon: MailIcon, brand: "#D93025", glyph: "#ffffff" },
  {
    toolkit: "googlecalendar",
    name: "Google Calendar",
    icon: CalendarIcon,
    brand: "#4285F4",
    glyph: "#ffffff",
  },
  { toolkit: "slack", name: "Slack", icon: MessageSquareIcon, brand: "#4A154B", glyph: "#ffffff" },
  {
    toolkit: "notion",
    name: "Notion",
    icon: FileTextIcon,
    brand: "var(--foreground)",
    glyph: "var(--background)",
  },
  {
    toolkit: "github",
    name: "GitHub",
    icon: GithubIcon,
    brand: "var(--foreground)",
    glyph: "var(--background)",
  },
];

// Cards for the wider set of slugs the recommendation engine can surface (dev,
// sales, and design profiles reach beyond the landing five). The remote Composio
// logo is what actually renders; brand/glyph/icon are only the fallback tile when
// that image fails to load. Keyed by slug so `appCardForToolkit` can look any up.
const EXTRA_APP_CARDS: ReadonlyArray<ConnectAppCardModel> = [
  { toolkit: "linear", name: "Linear", icon: LayersIcon, brand: "#5E6AD2", glyph: "#ffffff" },
  { toolkit: "jira", name: "Jira", icon: ListChecksIcon, brand: "#0052CC", glyph: "#ffffff" },
  { toolkit: "figma", name: "Figma", icon: PaletteIcon, brand: "#F24E1E", glyph: "#ffffff" },
  { toolkit: "hubspot", name: "HubSpot", icon: TagIcon, brand: "#FF7A59", glyph: "#ffffff" },
  {
    toolkit: "googledrive",
    name: "Google Drive",
    icon: HardDriveIcon,
    brand: "#1FA463",
    glyph: "#ffffff",
  },
  {
    toolkit: "googlesheets",
    name: "Google Sheets",
    icon: DatabaseIcon,
    brand: "#0F9D58",
    glyph: "#ffffff",
  },
  {
    toolkit: "googledocs",
    name: "Google Docs",
    icon: FileTextIcon,
    brand: "#4285F4",
    glyph: "#ffffff",
  },
  { toolkit: "outlook", name: "Outlook", icon: MailIcon, brand: "#0078D4", glyph: "#ffffff" },
  {
    toolkit: "microsoft_teams",
    name: "Microsoft Teams",
    icon: UsersIcon,
    brand: "#6264A7",
    glyph: "#ffffff",
  },
  { toolkit: "trello", name: "Trello", icon: LayoutGridIcon, brand: "#0052CC", glyph: "#ffffff" },
  { toolkit: "asana", name: "Asana", icon: ListChecksIcon, brand: "#F06A6A", glyph: "#ffffff" },
  {
    toolkit: "airtable",
    name: "Airtable",
    icon: ClipboardListIcon,
    brand: "#18BFFF",
    glyph: "#ffffff",
  },
  { toolkit: "dropbox", name: "Dropbox", icon: CloudIcon, brand: "#0061FF", glyph: "#ffffff" },
  {
    toolkit: "discord",
    name: "Discord",
    icon: MessageCircleIcon,
    brand: "#5865F2",
    glyph: "#ffffff",
  },
  { toolkit: "zoom", name: "Zoom", icon: CalendarDaysIcon, brand: "#2D8CFF", glyph: "#ffffff" },
];

/** Popular toolkits surfaced in the composer connect strip (order = priority). */
export const COMPOSER_FEATURED_TOOLKITS = [
  "gmail",
  "slack",
  "notion",
  "github",
  "googlecalendar",
  "linear",
  "jira",
  "figma",
  "googledrive",
  "googlesheets",
  "outlook",
  "microsoft_teams",
  "trello",
  "asana",
  "hubspot",
  "airtable",
  "dropbox",
  "discord",
  "zoom",
] as const;

const APP_CARD_BY_SLUG: ReadonlyMap<string, ConnectAppCardModel> = new Map(
  [...MORROW_APP_CARDS, ...EXTRA_APP_CARDS].map((card) => [card.toolkit, card]),
);

/** Title-case a bare slug for the fallback card name (e.g. "asana" -> "Asana"). */
function prettifySlug(slug: string): string {
  const trimmed = slug.trim();
  if (!trimmed) return "App";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * Resolve a card model for any toolkit slug. Known slugs get their curated
 * name/brand/icon; anything else gets a neutral fallback (the Composio remote
 * logo still renders on top, so unknown apps still look right).
 */
export function appCardForToolkit(slug: string): ConnectAppCardModel {
  return (
    APP_CARD_BY_SLUG.get(slug) ?? {
      toolkit: slug,
      name: prettifySlug(slug),
      icon: PlugIcon,
      brand: "var(--foreground)",
      glyph: "var(--background)",
    }
  );
}
